'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseCollapsibleHeaderOptions {
  /** Whether collapsible behavior is active */
  enabled: boolean;
  /** How small the header shrinks: 0.5 = 50% of original height */
  ratio: number;
}

/**
 * Internal hook that drives the collapsible header animation.
 *
 * Returns refs to attach to the wrapper and content elements, plus
 * a scroll handler and reset function. All DOM manipulation is done
 * directly on the refs for 60fps performance (no React state).
 */
export function useCollapsibleHeader({ enabled, ratio }: UseCollapsibleHeaderOptions) {
  /** Outer div — gets `height` set */
  const wrapperRef = useRef<HTMLDivElement>(null);
  /** Inner div — gets `transform: scale()` set */
  const contentRef = useRef<HTMLDivElement>(null);
  /** Stored natural height of the content */
  const fullHeightRef = useRef<number>(0);
  /** Bumped when the content ref attaches to trigger measurement */
  const [mounted, setMounted] = useState(false);

  /** Callback ref for the content div — triggers measurement when attached */
  const contentCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (node && enabled) setMounted(true);
    },
    [enabled]
  );

  /** Measure the content's natural height and store it */
  const measure = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;
    fullHeightRef.current = content.offsetHeight;
  }, []);

  /** Clear inline styles and re-measure on the next frame */
  const reset = useCallback(() => {
    if (!enabled) return;
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (wrapper) wrapper.style.height = '';
    if (content) content.style.transform = '';
    requestAnimationFrame(measure);
  }, [enabled, measure]);

  /**
   * Called with the current scrollTop of the content area.
   * Maps scroll distance to header shrink fraction and applies
   * height + scale directly to DOM nodes.
   */
  const handleScroll = useCallback(
    (scrollTop: number) => {
      if (!enabled) return;
      const wrapper = wrapperRef.current;
      const content = contentRef.current;
      if (!wrapper || !content) return;

      let full = fullHeightRef.current;
      if (!full) {
        // Lazy measurement — refs may not have been ready when the effect ran
        full = content.offsetHeight;
        fullHeightRef.current = full;
        if (!full) return;
      }

      const collapsed = full * ratio;
      const scrollRange = full - collapsed;
      const fraction = Math.min(Math.max(scrollTop / scrollRange, 0), 1);

      if (fraction === 0) {
        // Reset to natural sizing
        wrapper.style.height = '';
        content.style.transform = '';
        return;
      }

      const h = full + (collapsed - full) * fraction;
      const scale = 1 + (ratio - 1) * fraction;
      wrapper.style.height = `${h}px`;
      content.style.transform = `scale(${scale})`;
    },
    [enabled, ratio]
  );

  // Measure once the content ref is attached and observe resizes
  useEffect(() => {
    if (!enabled || !mounted) return;
    const content = contentRef.current;
    if (!content) return;

    // Initial measure after a frame so layout is settled
    const raf = requestAnimationFrame(measure);

    const observer = new ResizeObserver(measure);
    observer.observe(content);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [enabled, mounted, measure]);

  return { wrapperRef, contentCallbackRef, handleScroll, measure, reset };
}
